// **The reviewed round, as it arrives from the address bar** (`client-rounds-review` "The reviewed
// round is carried by the route, and is untrusted input"; `rounds-refresh-restore` tasks 5.6).
//
// The round used to live in component state, which is what made the defect silent: a reload lost it
// and fell back to the round the current phase reported, so a player reviewing round 2 during round 3
// was shown **round 3's trace presented as the round they asked for** — the wrong-trace-under-the-
// right-name class this capability already guards against for the subject colony.
//
// Moving it onto the route fixes that and creates a second obligation in the same move: the round is
// now **player-supplied input on a path that addresses another member's bytes**. So it is resolved
// here, as a pure function over the raw segment and the tournament's own length, before anything can
// build a locator out of it.
//
// This is not the authorization boundary and does not pretend to be one — `rounds-tournament-trace-read`
// answers `not_found` for a round the tournament does not have and for a colony outside the roster.
// What it removes is the client's ability to *construct* a request it already knows is meaningless,
// and a malformed path's ability to render as though it were a real round.

import type { ReviewSubject } from '../../ui/screens/RoundsReview';

/** Why a route's round cannot be reviewed. */
export type ReviewRoundRefusal =
  /** Not an integer at all — `/review/abc`, `/review/1.5`, `/review/` or a missing segment. */
  | 'malformed'
  /** An integer outside `[1, roundsTotal]`. */
  | 'out-of-range'
  /**
   * The tournament's length is not known yet, so the round cannot be validated.
   *
   * Distinct from the other two because it means something different to the player and must render
   * differently: nothing is wrong with the round they asked for, we simply cannot vouch for it yet.
   * It is a **pending** state, and it is transient — the other two are settled answers.
   */
  | 'total-unknown';

export type ReviewRoundResolution =
  | { readonly kind: 'round'; readonly round: number }
  | { readonly kind: 'unavailable'; readonly reason: ReviewRoundRefusal };

/**
 * Resolves a `/review/:round` segment against the tournament's length.
 *
 * **Fails closed.** A `null` total is not treated as "no upper bound" — it resolves to
 * `total-unknown`, so an unvalidated round can never reach a trace address just because the phase
 * payload had not landed yet. That is the direction the spec names explicitly, and it is the only safe
 * one: the permissive reading would let `/review/999` address a trace during exactly the window in
 * which the client cannot tell that 999 is not a round.
 *
 * The integer test is deliberately a **pattern match on the raw text**, not `Number.parseInt`.
 * `parseInt('3abc')` is `3` and `parseInt('')` is `NaN`, so parsing first and validating after would
 * accept `/review/3abc` as round 3 — reading a real trace under an address the player never typed.
 */
export function resolveReviewRound(
  raw: string | undefined,
  roundsTotal: number | null,
): ReviewRoundResolution {
  if (raw === undefined || !/^\d+$/.test(raw)) {
    return { kind: 'unavailable', reason: 'malformed' };
  }
  const round = Number(raw);
  if (!Number.isSafeInteger(round)) {
    // A segment of digits long enough to lose precision. `/^\d+$/` passed it; `Number` cannot
    // represent it exactly, so it is not the integer it appears to be.
    return { kind: 'unavailable', reason: 'malformed' };
  }
  if (roundsTotal === null) {
    return { kind: 'unavailable', reason: 'total-unknown' };
  }
  if (round < 1 || round > roundsTotal) {
    return { kind: 'unavailable', reason: 'out-of-range' };
  }
  return { kind: 'round', round };
}

/**
 * The graded subject for a **validated** round — the one place a graded trace address is built.
 *
 * Takes a `round: number`, never a resolution, so it is unreachable for a round that failed
 * validation: a malformed or out-of-range segment cannot construct a locator because there is no call
 * shape that would let it.
 *
 * `colony === null` yields the typed `gradedUnavailable` variant rather than a locator over a
 * sentinel (implementation review **F4**). `0` is a real colony, and because a tournament credential
 * may legitimately read *other* members' graded traces, substituting it does not fail — it succeeds,
 * and shows colony 0's trace labelled as the caller's own.
 */
export function gradedSubjectFor(
  round: number,
  colony: number | null,
  roundsTotal: number | null,
): ReviewSubject {
  return colony === null
    ? { mode: 'gradedUnavailable', round }
    : {
        mode: 'graded',
        locator: { kind: 'gradedRound', round, colony },
        subjectColony: colony,
        roundsTotal,
      };
}

/**
 * **Whether a round exists but has simply not been graded yet** (`client-rounds-review` "A round that
 * exists but is not yet graded is named as such, not as a credential failure").
 *
 * This exists because the server has one refusal for two very different situations.
 * `rounds-tournament-trace-read` serves a graded round "only once it is graded and complete" and
 * refuses an ungraded one **`unauthorized`** — and on this client `unauthorized` is the vocabulary of
 * *credential* failure, which the rounds root already branches into membership banners. Left
 * undistinguished, a player who bookmarks a round and comes back during the next round's editing
 * phase is told their membership failed: false, alarming, and with nothing for them to fix.
 *
 * The client can tell the two apart without asking, because it already knows where the tournament is:
 *
 *   * A round **earlier** than the live one has finished, so a refusal for it is genuinely about the
 *     credential.
 *   * A round **later** than the live one has not been played.
 *   * The **live** round is graded only once its results are out — the `standings` surface. During
 *     editing, simulating or reviewing it is still in progress.
 *
 * Returns `false` whenever the phase is unknown, which is the conservative direction: an unexplained
 * refusal is surfaced as the refusal it is, rather than excused as "not graded yet".
 */
export function roundIsNotGradedYet(
  round: number,
  phase: { readonly known: boolean; readonly round: number | null; readonly surface: string },
): boolean {
  if (!phase.known || phase.round === null) {
    return false;
  }
  if (round > phase.round) {
    return true;
  }
  return round === phase.round && phase.surface !== 'standings';
}

/** The route pattern. Named so the router, the navigators and the tests share one spelling. */
export const ROUNDS_REVIEW_ROUTE_PATTERN = '/review/:round';

/** The path for a given round — the one place a review link is spelled. */
export function roundsReviewPath(round: number): string {
  return `/review/${round}`;
}
